import { Badge, Box } from '@mantine/core';
import { useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { barve } from '../tema';

/**
 * Primerjava slike pred urejanjem in po njem: »potem« čez celo površino, »prej« levo od drsne črte (vlečenje z miško
 * ali prstom, puščici na tipkovnici). Obe sliki se prilagodita razmerju slike »potem«.
 */
export function PrejPotem({ prej, potem, visina }: { prej: string; potem: string; visina?: number | string }) {
  const [delez, setDelez] = useState(50);
  const okvir = useRef<HTMLDivElement>(null);
  const vlecem = useRef(false);

  const premakni = (x: number) => {
    const r = okvir.current?.getBoundingClientRect();
    if (r && r.width > 0) {
      setDelez(Math.min(100, Math.max(0, ((x - r.left) / r.width) * 100)));
    }
  };
  const zacni = (e: ReactPointerEvent<HTMLDivElement>) => {
    vlecem.current = true;
    e.currentTarget.setPointerCapture(e.pointerId);
    premakni(e.clientX);
  };

  const slika = { width: '100%', height: '100%', objectFit: 'contain', display: 'block', userSelect: 'none' } as const;
  return (
    <Box
      ref={okvir}
      role="slider"
      tabIndex={0}
      aria-label="Primerjava pred urejanjem in po njem"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(delez)}
      onPointerDown={zacni}
      onPointerMove={(e) => vlecem.current && premakni(e.clientX)}
      onPointerUp={() => (vlecem.current = false)}
      onPointerCancel={() => (vlecem.current = false)}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          setDelez((d) => Math.min(100, Math.max(0, d + (e.key === 'ArrowLeft' ? -5 : 5))));
        }
      }}
      style={{
        position: 'relative',
        height: visina,
        background: barve.izbrano,
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'ew-resize',
        touchAction: 'none',
      }}
    >
      <img src={potem} alt="Po urejanju" draggable={false} style={slika} />
      <img
        src={prej}
        alt="Pred urejanjem"
        draggable={false}
        style={{ ...slika, position: 'absolute', inset: 0, clipPath: `inset(0 ${100 - delez}% 0 0)` }}
      />
      <Box
        style={{
          position: 'absolute',
          top: 0,
          bottom: 0,
          left: `${delez}%`,
          width: 3,
          marginLeft: -1.5,
          background: barve.poudarek,
          boxShadow: '0 0 4px rgba(0,0,0,.4)',
          pointerEvents: 'none',
        }}
      >
        <Box
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: 28,
            height: 28,
            marginLeft: -14,
            marginTop: -14,
            borderRadius: '50%',
            background: barve.poudarek,
            border: '3px solid white',
            boxShadow: '0 1px 4px rgba(0,0,0,.4)',
          }}
        />
      </Box>
      <Badge
        pos="absolute"
        top={8}
        left={8}
        color="dark"
        variant="filled"
        style={{ pointerEvents: 'none', opacity: 0.8 }}
      >
        Prej
      </Badge>
      <Badge
        pos="absolute"
        top={8}
        right={8}
        color="dark"
        variant="filled"
        style={{ pointerEvents: 'none', opacity: 0.8 }}
      >
        Potem
      </Badge>
    </Box>
  );
}
